import React, { useMemo, useRef, useState } from "react";
import { ChatSession, ChatFolder } from "../../../services/api/chatHistory.api";
import SessionItem from "./SessionItem";
import FolderItem from "./FolderItem";
import NewFolderPopover from "./NewFolderPopover";
import { FolderOption } from "./FolderPickerPopover";

interface FolderWithSessions extends ChatFolder {
  sessions: ChatSession[];
  isOpen: boolean;
  isLoading: boolean;
}

interface ChatHistoryListProps {
  sessions: ChatSession[];
  folders: FolderWithSessions[];
  isLoading: boolean;
  error: string | null;
  activeSessionId: string | null;
  onSessionClick: (sessionId: string) => void;
  onSessionRename: (sessionId: string, newName: string) => void;
  onSessionDelete: (sessionId: string) => void;
  onFolderToggle: (folderId: number) => void;
  onFolderRename: (folderId: number, newName: string) => void;
  onFolderDelete: (folderId: number) => void;
  onCreateFolder: (name: string) => void;
  onMoveSession: (sessionId: string, folderId: number | null) => void; // This function is to move chat sessions to and from folders 
}

// Bucket sessions into This week / Older based on updated_at
function groupByTime(sessions: ChatSession[]) {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfWeek = new Date(startOfToday);
  startOfWeek.setDate(startOfToday.getDate() - startOfToday.getDay());

  const thisWeek: ChatSession[] = [];
  const older: ChatSession[] = [];

  for (const s of sessions) {
    const d = new Date(s.updated_at);
    if (d >= startOfWeek) thisWeek.push(s);
    else older.push(s);
  }
  return { thisWeek, older };
}

const ChatHistoryList: React.FC<ChatHistoryListProps> = ({
  sessions,
  folders,
  isLoading,
  error,
  activeSessionId,
  onSessionClick,
  onSessionRename,
  onSessionDelete,
  onFolderToggle,
  onFolderRename,
  onFolderDelete,
  onCreateFolder,
  onMoveSession, // This function is to move chat sessions to and from folders 
}) => {
  const { thisWeek, older } = useMemo(() => groupByTime(sessions), [sessions]);

  // Derive a flat folder list for the picker — passed down to every SessionItem
  const folderOptions: FolderOption[] = useMemo(
    () => folders.map((f) => ({ id: f.id, chat_folder_name: f.chat_folder_name })),
    [folders]
  );

  const [isNewFolderPopoverOpen, setIsNewFolderPopoverOpen] = useState(false);
  const addFolderBtnRef = useRef<HTMLButtonElement>(null);

  const renderGroup = (label: string, group: ChatSession[]) => {
    if (group.length === 0) return null;
    return (
      <div className="history-section" key={label}>
        <h3>{label}</h3>
        {group.map((s) => (
          <SessionItem
            key={s.session_id}
            session={s}
            isActive={s.session_id === activeSessionId}
            folders={folderOptions}
            onClick={onSessionClick}
            onRename={onSessionRename}
            onDelete={onSessionDelete}
            onMoveToFolder={(sessionId, folderId) => onMoveSession(sessionId, folderId)}
            // No onRemoveFromFolder here — these sessions are NOT inside a folder
          />
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="chat-history">
        <div className="chat-item chat-item--muted">
          <i className="fas fa-spinner fa-spin" />
          <span>Loading history…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chat-history">
        <div className="chat-item chat-item--muted">
          <i className="fas fa-exclamation-circle" />
          <span>Failed to load history</span>
        </div>
      </div>
    );
  }

  const hasAnything = sessions.length > 0 || folders.length > 0;

  return (
    <div className="chat-history">
      {/* Folders section */}
      <div className="history-section">
        <div className="folders-section-header">
          <h3>Folders</h3>
          <button
            ref={addFolderBtnRef}
            className="folder-add-btn"
            onClick={() => setIsNewFolderPopoverOpen((o) => !o)}
            title="New folder"
            aria-label="New folder"
          >
            <i className="fas fa-plus" />
          </button>
        </div>
        {folders.map((f) => (
          <FolderItem
            key={f.id}
            folder={f}
            activeSessionId={activeSessionId}
            folderOptions={folderOptions}
            onToggle={onFolderToggle}
            onSessionClick={onSessionClick}
            onSessionRename={onSessionRename}
            onSessionDelete={onSessionDelete}
            onRenameFolder={onFolderRename}
            onDeleteFolder={onFolderDelete}
            onMoveSession={onMoveSession} // This is to move seesion to and from folders
          />
        ))}
        <NewFolderPopover
          isOpen={isNewFolderPopoverOpen}
          anchorRef={addFolderBtnRef}
          onClose={() => setIsNewFolderPopoverOpen(false)}
          onConfirm={onCreateFolder}
        />
      </div>

      {/* Time-bucketed sessions (not in any folder) */}
      {renderGroup("This week", thisWeek)}
      {renderGroup("Older", older)}

      {!hasAnything && (
        <div className="history-section">
          <div className="chat-item chat-item--muted">
            <i className="fas fa-clock" />
            <span>No previous chats</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatHistoryList;